import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  IconButton,
  Grid,
  LinearProgress,
  Chip,
  Backdrop,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  AutoFixHigh,
  ContentCopy,
  Bolt,
  TrendingUp,
  AccountTreeOutlined,
  PsychologyOutlined,
  InfoOutlined,
} from "@mui/icons-material";
import axios from "axios";
import API_BASE_URL from "../config";

export default function SmartFlow(): JSX.Element {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [improvements, setImprovements] = useState<string[]>([]);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const handleEnhance = async () => {
    if (!inputText.trim()) return;
    setLoading(true);

    const prompt = `Analyze and improve the logical flow and transitions of the following text. Focus on:
1. Better transitions between ideas.
2. Improved sentence structure variety.
3. Enhanced academic coherence.
Provide the improved text first, then a short bulleted list of the main flow improvements made. 
Format: [IMPROVED TEXT] --- [IMPROVEMENTS]

Text: \n\n${inputText}`;

    try {
      const response = await axios.post(`${API_BASE_URL}/gemini-generate`, {
        prompt: prompt,
        model: "gemini-flash-latest",
      }, { timeout: 90000 });

      if (response.data && response.data.result) {
        const parts = response.data.result.split("---");
        setOutputText(parts[0].trim());
        if (parts[1]) {
          const bullets = parts[1]
            .split("\n")
            .filter(
              (line: string) =>
                line.trim().startsWith("-") || line.trim().startsWith("*"),
            );
          setImprovements(
            bullets.map((b: string) => b.replace(/^[-*]\s*/, "").trim()),
          );
        } else {
          setImprovements([
            "Improved logical mapping",
            "Enhanced transitional phrasing",
            "Strengthened vocabulary flow",
          ]);
        }
      }
    } catch (error) {
      console.error("Flow enhancement failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(outputText);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pt: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            trackingTight: "-0.02em",
            color: isDarkMode ? "white" : "text.primary",
          }}
        >
          Smart{" "}
          <span style={{ color: isDarkMode ? "#4b5563" : "#3b82f6" }}>
            Flow
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: isDarkMode ? "#9ca3af" : "text.secondary",
            fontWeight: 400,
            maxWidth: 600,
          }}
        >
          Enhance the structural coherence and logical transitions of your
          academic writing.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              bgcolor: isDarkMode ? "#030303" : "background.paper",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              height: 520,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ p: 3, flexGrow: 1 }}>
              <TextField
                fullWidth
                multiline
                placeholder="Paste your paragraph or draft here to improve its logical flow..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontFamily: "inherit",
                    fontSize: "15px",
                    lineHeight: 1.7,
                    color: isDarkMode ? "white" : "text.primary",
                    height: "100%",
                    alignItems: "flex-start",
                    "& textarea": { height: "100% !important" },
                  },
                }}
                sx={{ height: "100%" }}
              />
            </Box>
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Stack direction="row" spacing={1}>
                <Chip
                  icon={<PsychologyOutlined style={{ color: "#3b82f6" }} />}
                  label="Logic Analysis"
                  size="small"
                  sx={{
                    bgcolor: "rgba(59,130,246,0.05)",
                    color: "#3b82f6",
                    border: "1px solid rgba(59,130,246,0.1)",
                  }}
                />
              </Stack>
              <Button
                variant="contained"
                onClick={handleEnhance}
                disabled={loading || !inputText.trim()}
                startIcon={<TrendingUp />}
                sx={{
                  borderRadius: "10px",
                  bgcolor: isDarkMode ? "white" : "primary.main",
                  color: isDarkMode ? "black" : "white",
                  textTransform: "none",
                  px: 4,
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: isDarkMode ? "#e5e7eb" : "primary.dark",
                  },
                  "&.Mui-disabled": {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                    color: isDarkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.25)",
                  },
                }}
              >
                Enhance Flow
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack spacing={2} sx={{ height: "100%" }}>
            {/* Output Panel */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: "24px",
                bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                border: loading
                  ? "1px solid #3b82f6"
                  : `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
                minHeight: 300,
                transition: "border-color 0.3s",
              }}
            >
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 10,
                  }}
                >
                  <LinearProgress
                    sx={{
                      height: 2,
                      bgcolor: "transparent",
                      "& .MuiLinearProgress-bar": { bgcolor: "#3b82f6" },
                    }}
                  />
                </Box>
              )}
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: isDarkMode ? "#9ca3af" : "text.secondary",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <AutoFixHigh fontSize="small" sx={{ color: "#3b82f6" }} />{" "}
                  Coherent Output
                </Typography>
                <IconButton
                  onClick={handleCopy}
                  size="small"
                  disabled={!outputText}
                  sx={{ color: isDarkMode ? "#6b7280" : "text.secondary" }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
                <Typography
                  sx={{
                    fontSize: "15px",
                    lineHeight: 1.7,
                    color: outputText
                      ? isDarkMode
                        ? "white"
                        : "text.primary"
                      : isDarkMode
                        ? "#4b5563"
                        : "text.secondary",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {outputText ||
                    "The enhanced version focused on logical flow will appear here..."}
                </Typography>
              </Box>
            </Paper>

            {/* Performance/Insight Panel */}
            <Paper
              sx={{
                p: 3,
                borderRadius: "20px",
                bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "white" : "text.primary",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AccountTreeOutlined
                  fontSize="small"
                  sx={{ color: "#3b82f6" }}
                />{" "}
                Improvements Applied
              </Typography>
              <Stack spacing={1.5}>
                {improvements.length > 0 ? (
                  improvements.map((imp, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          width: 4,
                          height: 4,
                          borderRadius: "50%",
                          bgcolor: "#3b82f6",
                          mt: 1,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDarkMode ? "#9ca3af" : "text.secondary",
                        }}
                      >
                        {imp}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDarkMode ? "#4b5563" : "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    Scan text to see logic and flow improvements.
                  </Typography>
                )}
              </Stack>
            </Paper>

            <Box sx={{ px: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? "#4b5563" : "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <InfoOutlined fontSize="inherit" />
                Smart Flow focuses on transitional adverbs, logical sequencing,
                and syntactic variety.
              </Typography>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
        }}
        open={loading}
      >
        <CircularProgress color="inherit" size={60} thickness={4} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 500, letterSpacing: "0.05em" }}
        >
          Optimizing Flow & Logic...
        </Typography>
      </Backdrop>
    </Box>
  );
}
